import { i as isKeyboardUser } from "./focus-D9T8n_CN.js";
class HeaderV3Component extends HTMLElement {
  connectedCallback() {
    this._init();
  }
  _init() {
    this.cfg = {
      sticky: this.dataset.sticky === "true",
      stickyMode: this.dataset.stickyMode || "always",
      colorScheme: this.dataset.colorScheme || "color-scheme-1",
      stickyColorScheme: this.dataset.stickyColorScheme || "color-scheme-1",
      position: this.dataset.position || "outside",
      transparent: this.dataset.transparent || "fill",
      measureHeight: this.dataset.measureHeight !== "false"
    };
    this._toggle = this.querySelector(".header-toggle");
    this._nav = this.querySelector(".header-drawer");
    this._menus = this.querySelectorAll("menu-header-v2");
    this._isSticky = false;
    this._isHidden = false;
    this._isNavOpen = false;
    this._isHovered = false;
    this._scrollY = 0;
    this._isMobile = void 0;
    this._unsubs = [];
    this._setupMenuEvents();
    this._setupMobileNav();
    this._setupHoverScheme();
    let attempts = 0;
    const doInit = () => {
      const header = this.querySelector(".header-v3");
      if (!header) {
        if (++attempts > 60) {
          return;
        }
        requestAnimationFrame(doInit);
        return;
      }
      let actualHeight = 0;
      let isMobile = window.matchMedia("(max-width: 991px)").matches;
      if (header && this.cfg.measureHeight) {
        actualHeight = Math.round(header.getBoundingClientRect().height);
      }
      let rootFz = 16;
      let isMbWidth = window.innerWidth <= 991;
      let initialScrollY = window.scrollY;
      if (this.cfg.sticky) {
        rootFz = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      }
      this.classList.add(this.cfg.colorScheme);
      this._currentScheme = this.cfg.colorScheme;
      this._setupLogoColors();
      if (header && this.cfg.measureHeight) {
        if (isMobile) {
          document.documentElement.style.setProperty("--actual-header-height-mb", `${actualHeight}px`);
        } else {
          document.documentElement.style.setProperty("--actual-header-height", `${actualHeight}px`);
        }
        this._lastHeaderH = actualHeight;
        this._headerRo = new ResizeObserver((entries) => {
          const entry = entries[0];
          let h;
          if (entry.borderBoxSize && entry.borderBoxSize[0]) {
            h = Math.round(entry.borderBoxSize[0].blockSize);
          } else {
            h = Math.round(header.getBoundingClientRect().height);
          }
          if (this._lastHeaderH === h) return;
          this._lastHeaderH = h;
          const isMb = window.matchMedia("(max-width: 991px)").matches;
          if (isMb) {
            document.documentElement.style.setProperty("--actual-header-height-mb", `${h}px`);
          } else {
            document.documentElement.style.setProperty("--actual-header-height", `${h}px`);
          }
          if (this.cfg.sticky) this._updateSidebarTop();
        });
        this._headerRo.observe(header);
        this._unsubs.push(() => this._headerRo.disconnect());
        this._mutRAF = null;
        this._headerMo = new MutationObserver(() => {
          if (this._mutRAF) return;
          this._mutRAF = requestAnimationFrame(() => {
            this._mutRAF = null;
            this._measureHeaderHeight();
            if (this.cfg.sticky) this._updateSidebarTop();
          });
        });
        this._headerMo.observe(header, { childList: true, subtree: true });
        this._unsubs.push(() => {
          this._headerMo.disconnect();
          if (this._mutRAF) {
            cancelAnimationFrame(this._mutRAF);
            this._mutRAF = null;
          }
        });
      }
      if (this.cfg.sticky) {
        let stickyInitDone = false;
        const lazyInitSticky = () => {
          if (stickyInitDone) return;
          stickyInitDone = true;
          const bg = this.querySelector(".header-v3-bg");
          const bgSticky = this.querySelector(".header-v3-bg-sticky");
          if (bg && bgSticky) {
            this._bgStickyRo = new ResizeObserver(() => {
              requestAnimationFrame(() => {
                var _a;
                if (bgSticky.offsetWidth > 0) {
                  this.style.setProperty("--_scale-x", (bg.offsetWidth / bgSticky.offsetWidth).toFixed(4));
                  (_a = this._bgStickyRo) == null ? void 0 : _a.disconnect();
                }
              });
            });
            this._bgStickyRo.observe(bgSticky);
            this._unsubs.push(() => {
              var _a;
              return (_a = this._bgStickyRo) == null ? void 0 : _a.disconnect();
            });
          }
          this._initStickyConfig(rootFz, isMbWidth, window.scrollY);
        };
        if (initialScrollY > 0) {
          lazyInitSticky();
        } else {
          const onInteract = () => {
            ["scroll", "wheel", "touchmove"].forEach((evt) => window.removeEventListener(evt, onInteract));
            lazyInitSticky();
          };
          ["scroll", "wheel", "touchmove"].forEach((evt) => window.addEventListener(evt, onInteract, { passive: true }));
          this._updateSidebarTop();
        }
      }
      this.classList.add("is-ready");
    };
    requestAnimationFrame(doInit);
    this._resizeRAF = null;
    this._onResize = () => {
      if (this._resizeRAF) return;
      this._resizeRAF = requestAnimationFrame(() => {
        this._handleResize();
        this._resizeRAF = null;
      });
    };
    window.addEventListener("resize", this._onResize, { passive: true });
  }
  _setupLogoColors() {
    const items = (cls) => this.querySelectorAll(`.logo-header-v2-content-item.${cls}`);
    items("item-inverse").forEach((el) => el.classList.add(this.cfg.colorScheme));
    items("item-normal").forEach((el) => el.classList.add(this.cfg.stickyColorScheme));
  }
  // ─── Sticky ────────────────────────────────────────────────────────────────
  _initStickyConfig(rootFz, isMb, initialScrollY) {
    const STICKY = parseFloat(this.dataset[isMb ? "headerMgTopMb" : "headerMgTop"] || "0") * rootFz;
    const HIDE = isMb ? 50 : 100;
    let lastY = initialScrollY;
    if (this.cfg.stickyMode === "scroll-up") {
      this._handleScrollData = (d) => {
        if (this._isNavOpen) return;
        const y = d.scrollY;
        const delta = y - lastY;
        lastY = y;
        const wantSticky = y > STICKY;
        const locked = this._hasActiveDropdown();
        let wantHidden = this._isHidden;
        if (y <= HIDE || locked) wantHidden = false;
        else if (delta > 0) wantHidden = true;
        else if (delta < 0) wantHidden = false;
        if (wantSticky !== this._isSticky || wantHidden !== this._isHidden) {
          this._setSticky(wantSticky);
          this._setHidden(wantHidden);
        }
      };
    } else {
      this._handleScrollData = (d) => {
        if (this._isNavOpen) return;
        const wantSticky = d.scrollY > STICKY;
        if (wantSticky !== this._isSticky) {
          this._setSticky(wantSticky);
        }
      };
    }
    this._handleScrollData({ scrollY: window.scrollY });
    this._updateSidebarTop();
    this._bindToDispatcher();
  }
  _bindToDispatcher() {
    var _a;
    if ((_a = window.Theme) == null ? void 0 : _a.onScrollUpdate) {
      this._unsubs.push(window.Theme.onScrollUpdate(this._handleScrollData));
    } else {
      const handler = (e) => {
        window.removeEventListener("scroll-dispatcher:ready", handler);
        this._unsubs.push(
          e.detail.dispatcher.on("update", this._handleScrollData)
        );
      };
      window.addEventListener("scroll-dispatcher:ready", handler);
      this._unsubs.push(() => window.removeEventListener("scroll-dispatcher:ready", handler));
    }
  }
  _setSticky(on) {
    if (this._isSticky === on) return;
    this._isSticky = on;
    this._scheduleDomUpdate();
  }
  _setHidden(on) {
    if (this._isHidden === on) return;
    this._isHidden = on;
    this._scheduleDomUpdate();
    if (on) this._closeMenus();
  }
  _scheduleDomUpdate() {
    if (this._domUpdateRAF) return;
    this._domUpdateRAF = requestAnimationFrame(() => {
      this._domUpdateRAF = null;
      this.classList.toggle("is-sticky", this._isSticky);
      this.classList.toggle("is-hidden", this._isHidden);
      this._updateSidebarTop();
      this._updateColorScheme();
    });
  }
  /**
   * Push --sidebar-top on <body> so the sidebar can position itself
   * without :has() selectors (which pierce containment).
   *
   * When measureHeight is enabled the value references the actual measured
   * header height (--actual-header-height / --actual-header-height-mb),
   * otherwise it falls back to the static --header-minheight calculation.
   *
   * Rules (content-gap is always added as sidebar padding-top):
   *  - No [data-sticky-top]   → remove property (consumers fall back to default)
   *  - Not sticky             → remove property (same as no-header)
   *  - Sticky + always        → actual header height (or min-height fallback)
   *  - Sticky + scroll-up     → actual header height (or min-height fallback)
   *  - Sticky + scroll-up + hidden → remove property (same as no-header)
   */
  _updateSidebarTop() {
    if (this.cfg.stickyMode === "scroll-up" && this._isHidden) {
      document.body.style.removeProperty("--sidebar-top");
      this._lastSidebarTop = null;
      return;
    }
    const isMb = window.innerWidth <= 991;
    let hVar;
    if (isMb) {
      hVar = "var(--header-minheight-mb, 3.25rem)";
    } else {
      hVar = this.cfg.measureHeight ? "var(--actual-header-height, var(--header-minheight, 3.75rem))" : "var(--header-minheight, 3.75rem)";
    }
    const newVal = `calc(${hVar} + var(--section-gap, 0px))`;
    if (this._lastSidebarTop === newVal) return;
    this._lastSidebarTop = newVal;
    document.body.style.setProperty("--sidebar-top", newVal);
  }
  // ─── Color Scheme ──────────────────────────────────────────────────────────
  _updateColorScheme() {
    if (this.cfg.position !== "inside" || this.cfg.transparent !== "transparent") return;
    const bgVisible = this._isSticky || this._hasActiveDropdown() || this._isNavOpen || this._isHovered;
    const target = bgVisible ? this.cfg.stickyColorScheme : this.cfg.colorScheme;
    if (this._currentScheme === target) return;
    const prev = this._currentScheme;
    this._currentScheme = target;
    if (prev) this.classList.replace(prev, target);
    else this.classList.add(target);
  }
  /** Track header hover for display-bg-when-hover so color scheme follows the bg. */
  _setupHoverScheme() {
    if (this.dataset.displayBgWhenHover !== "true") return;
    this.addEventListener("mouseenter", () => {
      this._isHovered = true;
      this._updateColorScheme();
    });
    this.addEventListener("mouseleave", () => {
      this._isHovered = false;
      this._updateColorScheme();
    });
  }
  // ─── Menu Events ───────────────────────────────────────────────────────────
  /** Check actual DOM for any active dropdown or megamenu (no counter needed). */
  _hasActiveDropdown() {
    return !!(this.querySelector(".menu-header-v3-item.is-dropdown-active") || this.querySelector(".menu-header-v3-item.is-megamenu-active") || this.querySelector(".megamenu-v2.active") || this.querySelector("dropdown-localization-component.i18n-active"));
  }
  _setupMenuEvents() {
    this.addEventListener("dropdown:open", () => {
      clearTimeout(this._menuCloseTimer);
      this.classList.add("is-menu-open");
      this._updateColorScheme();
    });
    this.addEventListener("dropdown:close", () => {
      clearTimeout(this._menuCloseTimer);
      this._menuCloseTimer = setTimeout(() => {
        if (!this._hasActiveDropdown()) {
          this.classList.remove("is-menu-open", "is-megamenu-active");
        }
        this._updateColorScheme();
      }, 150);
    });
  }
  _closeMenus() {
    this._menus.forEach((m) => m.dispatchEvent(new CustomEvent("close-all-dropdowns")));
    this.classList.remove("is-menu-open", "is-megamenu-active");
  }
  // ─── Mobile Nav ────────────────────────────────────────────────────────────
  _setupMobileNav() {
    if (!this._toggle || !this._nav) return;
    this._toggle.addEventListener("click", () => this._toggleNav());
  }
  _toggleNav() {
    this._isNavOpen = !this._isNavOpen;
    const open = this._isNavOpen;
    this._nav.classList.toggle("active", open);
    this._toggle.classList.toggle("active", open);
    this.classList.toggle("is-open", open);
    this._toggle.setAttribute("aria-expanded", String(open));
    this._toggle.setAttribute("aria-label", open ? "Close navigation menu" : "Open navigation menu");
    const isMobile = window.innerWidth <= 991;
    if (isMobile) {
      this._nav.setAttribute("aria-hidden", String(!open));
      this._nav.inert = !open;
      if (open && isKeyboardUser()) {
        const first = this._nav.querySelector('a, button, [tabindex="0"]');
        if (first) first.focus();
      }
    } else {
      this._nav.removeAttribute("aria-hidden");
      this._nav.inert = false;
    }
    if (open) {
      const navTop = this._nav.getBoundingClientRect().top;
      const margin = parseFloat(getComputedStyle(this._nav).marginBottom) || 0;
      this.style.setProperty("--nav-top-offset", `${navTop + margin}px`);
      this._lockScroll();
    } else {
      this._unlockScroll();
      this._menus.forEach((m) => {
        var _a;
        return (_a = m._resetMobileNavigation) == null ? void 0 : _a.call(m);
      });
    }
    this._updateColorScheme();
  }
  /** Close the mobile nav programmatically (e.g. from external components). */
  closeMobileNav() {
    if (this._isNavOpen) this._toggleNav();
  }
  _lockScroll() {
    var _a;
    if ((_a = window.Theme) == null ? void 0 : _a.lenis) {
      window.Theme.lenis.stop();
    } else {
      this._scrollY = window.pageYOffset;
      Object.assign(document.body.style, {
        overflow: "hidden",
        position: "fixed",
        top: `-${this._scrollY}px`,
        width: "100%"
      });
    }
  }
  _unlockScroll() {
    var _a;
    if ((_a = window.Theme) == null ? void 0 : _a.lenis) {
      window.Theme.lenis.start();
    } else {
      Object.assign(document.body.style, { overflow: "", position: "", top: "", width: "" });
      window.scrollTo(0, this._scrollY);
    }
  }
  // ─── Resize ────────────────────────────────────────────────────────────────
  _handleResize() {
    var _a, _b;
    const isMobile = window.innerWidth <= 991;
    const wasInitialized = this._isMobile !== void 0;
    if (this._isMobile === isMobile) return;
    this._isMobile = isMobile;
    if (!wasInitialized) return;
    if (this._isNavOpen) {
      (_a = this._nav) == null ? void 0 : _a.classList.remove("active");
      (_b = this._toggle) == null ? void 0 : _b.classList.remove("active");
      this.classList.remove("is-open");
      this._isNavOpen = false;
      this._unlockScroll();
      this._menus.forEach((m) => {
        var _a2;
        return (_a2 = m._resetMobileNavigation) == null ? void 0 : _a2.call(m);
      });
    }
    this._closeMenus();
    this._updateColorScheme();
    this._measureHeaderHeight();
    if (this.cfg.sticky) this._updateSidebarTop();
  }
  /** Measure the rendered header height and write only the current breakpoint's var.
   *  Called on breakpoint cross so each viewport gets its correct value. */
  _measureHeaderHeight() {
    if (!this.cfg.measureHeight) return;
    const header = this.querySelector(".header-v3");
    if (!header) return;
    let h;
    if (this._lastHeaderH !== void 0) {
      h = this._lastHeaderH;
    } else {
      h = Math.round(header.getBoundingClientRect().height);
    }
    const isMb = window.matchMedia("(max-width: 991px)").matches;
    if (isMb) {
      document.documentElement.style.setProperty("--actual-header-height-mb", `${h}px`);
    } else {
      document.documentElement.style.setProperty("--actual-header-height", `${h}px`);
    }
  }
  // ─── Cleanup ───────────────────────────────────────────────────────────────
  disconnectedCallback() {
    this._unsubs.forEach((fn) => fn());
    window.removeEventListener("resize", this._onResize);
    if (this._resizeRAF) cancelAnimationFrame(this._resizeRAF);
    if (this._isNavOpen) this._unlockScroll();
  }
}
if (!customElements.get("header-v3-component")) {
  customElements.define("header-v3-component", HeaderV3Component);
}
